/**
 * Creating ADDITIONAL organizations and tenants is a plan feature — the ONE
 * place the dashboard asks.
 *
 * A customer's first organization and tenant come with signup (onboarding,
 * never gated). Creating more is the `tenancy_creation` entitlement, which
 * management-api's authorizer enforces on `POST /organizations` and
 * `POST /organizations/{id}/tenants` with a `PERM003` refusal. This module is
 * the UI half: whether to OFFER those controls, and what to say when the API
 * refuses one anyway.
 *
 * **Hidden, not upsold.** Without the entitlement the create controls are not
 * rendered at all — no disabled button, no "upgrade" line. Every call site
 * (the shell's org menu, Settings › Organizations & tenants, and the create
 * dialog itself) asks {@link useCanCreateTenancy}; `tenancyCreation.test.tsx`
 * holds every mount of `CreateTenantDialog` to that.
 *
 * Only the ENTITLEMENT is decided here. Whether this person may create one
 * (`tenants:create` / `users:create`) stays at the call sites, beside the
 * controls it gates — a plan and a role are different questions with different
 * remedies.
 *
 * Distinct from `tenancy_switching`, which packages the switcher and the
 * tenancy admin surface as a whole. A plan can let someone move between the
 * tenants they have without letting them make more.
 *
 * The answer comes from `useEntitlement`, so it follows `<DashboardPlan>` — and
 * with no provider, or a plan not yet known, it is `true`. Unknown is not free:
 * see `PlanProvider`.
 */
import { useEntitlement } from "@authe/ui-core";
import { ENTITLEMENT } from "@authe/fe-commons/plans";
import { ErrorCode, errorCodeOf, toErrorResponse } from "@authe/fe-commons/errors";

/** Does the caller's plan let them create additional organizations and tenants? */
export function useCanCreateTenancy(): boolean {
  return useEntitlement(ENTITLEMENT.TENANCY_CREATION);
}

/** The plan message for a refused tenant creation. */
export const TENANT_CREATION_NOT_INCLUDED = "Creating tenants isn't included in your plan.";

const PERM003 = ErrorCode.PERM003!.code;

/**
 * Is `error` the API refusing a tenancy creation because the plan lacks it?
 *
 * Reached when the plan changed mid-session, or this tab's copy of it was
 * stale: the control was offered, the authorizer said no.
 *
 * Read through `toErrorResponse` / `errorCodeOf` because the envelope carries
 * two codes and the catalogued one may be in either.
 *
 * Two wire shapes reach here, and both must be recognised:
 *
 *  - a HANDLER refusal (`commons.authz.require_entitlements`) names the
 *    entitlement in `details.missing_entitlements` — so when that list is
 *    present it has to include `tenancy_creation`, or this is some other
 *    feature's refusal and is not ours to rephrase;
 *  - the AUTHORIZER refusal these routes actually get goes out through the
 *    shared API Gateway `ACCESS_DENIED` template, which carries `error_code`
 *    and a flat `details` STRING (`"Required: tenancy_creation"`) but no
 *    `missing_entitlements` list — API Gateway context values cannot be arrays.
 *    A `PERM003` with no list, from a create call gated on exactly this
 *    entitlement, is this refusal.
 */
export function isTenancyCreationRefusal(error: unknown): boolean {
  if (error == null) return false;
  const body = toErrorResponse(error).error;
  if (errorCodeOf(body) !== PERM003) return false;
  const details = body.details as unknown;
  const missing =
    details && typeof details === "object"
      ? (details as { missing_entitlements?: unknown }).missing_entitlements
      : undefined;
  if (Array.isArray(missing)) return missing.includes(ENTITLEMENT.TENANCY_CREATION);
  return true;
}

/** The plan message for a tenant-creation refusal, else `undefined`. */
export function tenantCreationRefusalMessage(error: unknown): string | undefined {
  return isTenancyCreationRefusal(error) ? TENANT_CREATION_NOT_INCLUDED : undefined;
}
